import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Stack, SxProps, Theme, Typography, useMediaQuery } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BEIGE_300, BEIGE_700, GRAY_700, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import { MUITooltip } from 'components/common/MUITooltip'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import { PropertyAddressDTO, RealEstatePropertyDTO, RealEstatePropertyListItemDTO } from 'models/property'
import type { CountryCode } from 'models/purchaseFlowRevamp'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'
import { usePfStore } from 'components/pages/PurchaseFlowRevamp/store'

import { PfRevampEventName, type PropertyCreateSource } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'
import { PfStepPath, usePfPath } from '../routing/pfBasePath'
import { PfDrawerScrim } from '../components/PfDrawerScrim.component'
import { DraftOrdersSection } from '../drafts/DraftOrdersSection.component'
import { AdminTargetBanner } from './AdminTargetBanner.component'
import { ClientSelectionSection, type ClientSelection } from './ClientSelectionSection.component'
import { CreatePropertyForm, type CreatePropertyHandle } from './CreatePropertyForm.component'
import { PropertyCompletionForm, type PropertyCompletionHandle, type PropertyCompletionResult } from './PropertyCompletionForm.component'
import { PropertySearchBox, usePropertySearchBox } from './PropertySearchBox.component'
import { PropertySelectionItem } from './PropertySelectionItem.component'
import { type PropertySelection, type SelectedProperty, useCreatePropertyInDrawer } from './useCreatePropertyInDrawer'
import { formatPropertyAddress } from './useEmbedPresetProperty'
import { usePfEffectiveWorkspaceId } from './usePfEffectiveWorkspaceId'
import { usePropertyCompleteness } from './usePropertyCompleteness'

interface Props {
  /** Whether the drawer is open */
  isOpen: boolean
  /** Callback to close the drawer, on both dismissal and proceeding */
  onClose: () => void
  /** Called when the drawer is dismissed without proceeding (close button, Escape) */
  onDismiss?: () => void
  /** Replaces the default navigation to the catalogue, for a drawer stacked above the product drawer. The store write and close still run. */
  onProceed?: (selection: { id?: string; addressLabel: string; countryCode: CountryCode }) => void
  /** Forwarded to the drawer root, e.g. to raise it above the product drawer. */
  sx?: SxProps<Theme>
  /** Opened over another drawer, which must stay mounted underneath. */
  allowStacking?: boolean
  /**
   * The property this order is for, decided outside the drawer (the Propstack
   * embed). It is pre-selected and cannot be searched away, removed or replaced
   * by a new one — the drawer only lets the client complete it.
   */
  lockedProperty?: RealEstatePropertyDTO
  /** False inside the embed: closing would leave the order with nowhere to go. Defaults to true. */
  closable?: boolean
  /** Selected whenever the drawer opens, e.g. the order's property when it turned out incomplete. Unlike `lockedProperty`, the client may still pick another. */
  preselectedProperty?: SelectedProperty
  /** Lists the client's saved orders under the search, for the "New order" entry. */
  showDraftOrders?: boolean
  /** The completion form saved what the property lacked, just before the drawer proceeds with it. */
  onCompletionSaved?: (result: PropertyCompletionResult) => void
}

/**
 * Choose the order's property: search the workspace's properties, create one, or complete one whose
 * address cannot be ordered yet. "Next step" stores it and opens the catalogue.
 *
 * An admin ordering for a client first picks the client in the same drawer, because every property
 * query is scoped to the client's workspace and an admin has none of their own.
 */
export const PropertySelectionDrawer: React.FC<Props> = ({ isOpen, onClose, onDismiss, onProceed, sx, allowStacking, lockedProperty, closable = true, preselectedProperty, showDraftOrders = false, onCompletionSaved }) => {
  const { t } = useTranslation(['order', 'purchase_flow_revamp', 'new_property', 'common'])
  const navigate = useNavigate()
  const pfPath = usePfPath()
  const { roles } = useAuth0()
  // On a phone the sheet dims the page like the flow's other sheets; stacked over the product drawer, that one's scrim is already there.
  const showsScrim = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY) && !allowStacking

  const workspaceId = usePfEffectiveWorkspaceId()
  const setProperty = usePfStore((s) => s.setProperty)
  const adminTargetEmail = usePfStore((s) => s.targetUserEmail)
  const adminTargetWorkspaceId = usePfStore((s) => s.targetWorkspaceId)
  const adminTargetInternalId = usePfStore((s) => s.targetUserInternalId)

  const [isCreateMode, setIsCreateMode] = useState(false)
  const [createAddressQuery, setCreateAddressQuery] = useState('')
  const [isChangingClient, setIsChangingClient] = useState(false)
  const [targetWorkspaceName, setTargetWorkspaceName] = useState('')

  // The workspace, the email and the client's internal id are needed: a resumed draft restores only the workspace,
  // and a target stored before the internal id existed cannot name the client as broker of a new property.
  const needsClientSelection = roles.isAdmin && !(adminTargetWorkspaceId && adminTargetEmail && adminTargetInternalId)
  const showClientStage = roles.isAdmin && (needsClientSelection || isChangingClient)

  useEffect(() => {
    if (roles.isAdmin && adminTargetWorkspaceId && adminTargetEmail && !adminTargetInternalId) {
      logger.warn('pf_revamp property drawer: admin target has no internal id, client selection reopened', undefined, { context: { component: 'PropertySelectionDrawer', action: 'client_gate', metadata: { targetWorkspaceId: adminTargetWorkspaceId } } })
    }
  }, [roles.isAdmin, adminTargetWorkspaceId, adminTargetEmail, adminTargetInternalId])

  const handleClientSelected = useCallback((selection: ClientSelection) => {
    setTargetWorkspaceName(selection.workspaceName)
    setIsChangingClient(false)
  }, [])

  const handleClose = useCallback(() => {
    // Escape still asks to close even with the close button hidden.
    if (!closable) return
    setIsChangingClient(false)
    onDismiss?.()
    onClose()
  }, [onClose, onDismiss, closable])

  const [selectedProperty, setSelectedProperty] = useState<SelectedProperty | null>(() => lockedProperty
    ? {
      id: lockedProperty.id,
      name: lockedProperty.title || formatPropertyAddress(lockedProperty.address),
      address: formatPropertyAddress(lockedProperty.address),
      countryCode: (lockedProperty.address?.country ?? 'DE') as CountryCode,
      regionCode: lockedProperty.address?.regionCode,
      title: lockedProperty.title,
      objectReferenceId: lockedProperty.objectReferenceId,
    }
    : null)

  useEffect(() => {
    if (isOpen && preselectedProperty) setSelectedProperty(preselectedProperty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Any saved property is checked for an orderable address, including one just created that was not provably orderable.
  const isPreexistingSelected = !!(selectedProperty?.id && !selectedProperty.newPropertyData)
  const completeness = usePropertyCompleteness(
    isPreexistingSelected ? (selectedProperty?.id ?? null) : null,
    workspaceId || null
  )

  const search = usePropertySearchBox((property: RealEstatePropertyListItemDTO) => {
    setSelectedProperty({
      id: property.id,
      name: property.title || property.address?.street || property.address?.postalCode || property.address?.country || '',
      address: formatPropertyAddress(property.address),
      countryCode: (property.address?.country ?? 'DE') as CountryCode,
      regionCode: property.address?.regionCode,
      title: property.title,
      objectReferenceId: property.objectReferenceId,
    })
  })

  /** Clients search by address, so what they typed starts the new property's address search. */
  const openCreateForm = useCallback((source: PropertyCreateSource) => {
    const query = search.searchText.trim()
    setCreateAddressQuery(query)
    setIsCreateMode(true)
    dispatchPfRevampEvent({
      type: PfRevampEventName.PROPERTY_CREATE_OPENED,
      payload: { source, prefilled: !!query, queryLength: query.length },
    })
  }, [search.searchText])

  /** Stores the property, closes, and goes on — to the catalogue, or wherever `onProceed` says. */
  const proceedWithProperty = useCallback((selection: PropertySelection) => {
    setProperty(selection)
    onClose()
    if (onProceed) onProceed(selection)
    else navigate(pfPath(PfStepPath.CATALOGUE))
  }, [setProperty, onClose, onProceed, navigate, pfPath])

  const handleNeedsCompletion = useCallback((selected: SelectedProperty) => {
    setIsCreateMode(false)
    setSelectedProperty(selected)
  }, [])

  const createProperty = useCreatePropertyInDrawer(workspaceId, adminTargetInternalId ?? undefined, {
    onOrderable: proceedWithProperty,
    onNeedsCompletion: handleNeedsCompletion,
  })

  const showCompletionForm = isPreexistingSelected && !completeness.isComplete && !completeness.isLoading
    && !completeness.isError && completeness.missing.length > 0
  const completionRef = useRef<PropertyCompletionHandle>(null)
  const [completionState, setCompletionState] = useState({ canSave: false, isSaving: false })

  const createRef = useRef<CreatePropertyHandle>(null)
  const [canCreate, setCanCreate] = useState(false)
  const handleCreateState = useCallback(({ canSave }: { canSave: boolean }) => setCanCreate(canSave), [])

  const handleProceed = useCallback(async () => {
    if (isCreateMode) {
      createRef.current?.submit()
      return
    }
    if (!selectedProperty) return

    let savedCoordinates: { lat: number; lng: number } | undefined
    let savedAddress: PropertyAddressDTO | undefined
    if (showCompletionForm) {
      const saved = await completionRef.current?.submit()
      if (!saved) return
      savedCoordinates = saved.coordinates
      savedAddress = saved.address
      onCompletionSaved?.(saved)
    }

    proceedWithProperty({
      id: selectedProperty.id,
      // A re-entered address replaces the incomplete one the property was listed with.
      addressLabel: savedAddress ? formatPropertyAddress(savedAddress) : selectedProperty.address,
      countryCode: (savedAddress?.country as CountryCode | undefined) ?? selectedProperty.countryCode,
      regionCode: savedAddress ? savedAddress.regionCode : selectedProperty.regionCode,
      title: selectedProperty.title,
      objectReferenceId: selectedProperty.objectReferenceId,
      // From the detail the completeness check fetched; it refuses to get here without them.
      coordinates: savedCoordinates ?? completeness.coordinates,
      routingCoordinates: completeness.routingCoordinates,
    })
  }, [isCreateMode, selectedProperty, showCompletionForm, proceedWithProperty, completeness.coordinates, completeness.routingCoordinates, onCompletionSaved])

  /**
   * "Next step" waits for a saved property's detail, because its coordinates ride on the same request
   * and committing without them makes every later instruction-options request fail for the session.
   * A failed lookup lets the client through rather than trapping them; the coordinates are recovered
   * later (`useResolvedPropertyCoordinates`).
   */
  const isProceedDisabled = isCreateMode
    ? !canCreate || createProperty.isPending
    : !selectedProperty || (showCompletionForm
      ? !completionState.canSave || completionState.isSaving
      : isPreexistingSelected && (completeness.isLoading || (!completeness.isComplete && !completeness.isError)))

  return (
    <>

      {isOpen && showsScrim &&
        <PfDrawerScrim />
      }

      <ResponsiveDrawer
        isOpen={isOpen}
        onClose={handleClose}
        useDefaultCloseButton={closable}
        title={showClientStage
          ? t('purchase_flow_revamp:admin_gate.title')
          : t('purchase_flow_revamp:select_property_title')}
        subTitleSlot={!showClientStage && !isCreateMode && !lockedProperty &&
          <Typography variant="text-sm" color={GRAY_700}>
            {t('purchase_flow_revamp:drawer.property_select_subtitle')}
          </Typography>
        }
        width="48rem"
        sx={sx}
        allowStacking={allowStacking}
        headerSx={{
          backgroundColor: BEIGE_300,
          borderBottomColor: BEIGE_700,
          '& > .MuiStack-root': { alignItems: 'center' }
        }}
        disableBackdropClick
        footerSx={{
          flexDirection: 'column',
          alignItems: 'stretch',
          borderTop: 'none',
          padding: 0,
          gap: '1.2rem'
        }}
        actionsSlot={showClientStage
          ? undefined
          : (
            <>
              <MUIDivider margin={0} />

              <Stack direction="row" justifyContent="flex-end" px="2rem" pb="1.2rem">
                <MUITooltip
                  content={showCompletionForm && !completionState.canSave ? t('purchase_flow_revamp:drawer.complete_property_save_hint') : ''}
                  placement="top"
                >
                  <MUIButton
                    size="md"
                    disabled={isProceedDisabled}
                    onClick={() => { void handleProceed() }}
                  >
                    {isCreateMode
                      ? t(createProperty.isPending ? 'purchase_flow_revamp:creating_property' : 'purchase_flow_revamp:drawer.complete_property_save_and_continue')
                      : showCompletionForm
                        ? t(completionState.isSaving ? 'purchase_flow_revamp:drawer.completing_property' : 'purchase_flow_revamp:drawer.complete_property_save_and_continue')
                        : t('purchase_flow_revamp:next_step')}
                  </MUIButton>
                </MUITooltip>
              </Stack>
            </>
          )}
      >
        <Stack spacing={0}>

          {showClientStage &&
            <ClientSelectionSection
              onSelected={handleClientSelected}
              onCancel={isChangingClient ? () => setIsChangingClient(false) : undefined}
            />
          }

          {!showClientStage && roles.isAdmin && !!adminTargetEmail && !isCreateMode &&
            <AdminTargetBanner email={adminTargetEmail} workspaceName={targetWorkspaceName} onChange={() => setIsChangingClient(true)} />
          }

          {!showClientStage && isCreateMode &&
            <CreatePropertyForm
              ref={createRef}
              hideSubmit
              onStateChange={handleCreateState}
              onClose={() => setIsCreateMode(false)}
              onSubmit={createProperty.submit}
              isSubmitting={createProperty.isPending}
              initialAddressQuery={createAddressQuery}
            />
          }

          {!showClientStage && !isCreateMode &&
            <>

              {!lockedProperty &&
                <PropertySearchBox search={search} onCreateNew={openCreateForm} />
              }

              {showDraftOrders && !lockedProperty && !selectedProperty &&
                <DraftOrdersSection onResumed={onClose} />
              }

              {!!selectedProperty &&
                <Stack spacing={1} sx={{ mt: lockedProperty ? 0 : '1.6rem' }}>

                  <Typography variant="text-sm" color={GRAY_700} fontWeight={450}>
                    {t('order:step_address.selected_property')}
                  </Typography>

                  <PropertySelectionItem
                    name={selectedProperty.name}
                    address={selectedProperty.address}
                    onRemove={lockedProperty ? undefined : () => setSelectedProperty(null)}
                  />

                  {showCompletionForm &&
                    <PropertyCompletionForm
                      ref={completionRef}
                      hideSubmit
                      onStateChange={setCompletionState}
                      propertyId={selectedProperty.id!}
                      missing={completeness.missing}
                      // The form invalidates the property query; completeness re-evaluates from it.
                      onCompleted={() => {}}
                    />
                  }

                </Stack>
              }

            </>
          }

        </Stack>
      </ResponsiveDrawer>

    </>
  )
}
